/**
 * "Did you mean name@gmail.com?" under the email field of every contact form.
 *
 * Never changes the address unless the shopper clicks. A known typo pauses the first submit
 * and offers "Keep as typed"; an address with no ending can't be submitted until it's fixed.
 *
 * Public API on `window.ContactEmailCheck`:
 *
 *   hydrate(scope)        -> mount every un-mounted form[data-contact-form] in scope
 *   destroyWithin(scope)  -> tear down the forms inside scope
 *   instances             -> Map<form id, EmailCheck>
 *   get(formId)           -> one instance
 *   getState(formId)      -> { formId, email, suggestions, blocking, kept }
 *   suggest(email)        -> { list, block } or null
 *
 * Events (bubble on document), with detail { state, previous }:
 *
 *   contact-email:change  -> the prompt appeared, changed, or was cleared
 */
(() => {
  const facade = window.ContactEmailCheck || {};

  if (facade.instances) return;

  /* typo lists built from Kustomer bounces, Oct 5 – Nov 4, 2026 */
  const DOMAIN_FIX = {
    'gnail.com': 'gmail.com',
    'gamil.com': 'gmail.com',
    'gmaill.com': 'gmail.com',
    'agmail.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gmial.com': 'gmail.com',
    'gma.com': 'gmail.com',
    'gmal.com': 'gmail.com',
    'gmil.com': 'gmail.com',
    'yahoo.co': 'yahoo.com',
    'yahoo.cm': 'yahoo.com',
    'yahoi.com': 'yahoo.com',
    'yhoo.com': 'yahoo.com',
    'yahop.com': 'yahoo.com',
    'yajoo.com': 'yahoo.com',
    'yanoo.com': 'yahoo.com',
    'yahhoo.com': 'yahoo.com',
    'yaboo.com': 'yahoo.com',
    'yaho.com': 'yahoo.com',
    'icloid.com': 'icloud.com',
    'iclod.com': 'icloud.com',
    'ioud.com': 'icloud.com',
    'icoud.com': 'icloud.com',
    'hotmal.com': 'hotmail.com',
    'hotmial.com': 'hotmail.com',
    'outlok.com': 'outlook.com',
    'outloo.com': 'outlook.com',
    'aop.com': 'aol.com',
    'apl.com': 'aol.com',
  };
  const AMBIGUOUS = {
    'hmail.com': ['gmail.com', 'hotmail.com'],
    'fmail.com': ['gmail.com'],
    'vmail.com': ['gmail.com'],
  };
  const PROVIDERS = ['gmail', 'yahoo', 'hotmail', 'outlook', 'icloud', 'aol', 'live', 'msn', 'comcast', 'me'];
  const BAD_ENDING = /^(con|clm|vom|ckm|xom|cok|ckn|cpm|om|cm|co|c|com.+)$/;

  const normalize = (email) => String(email || '').trim().toLowerCase();

  function suggest(email) {
    const match = normalize(email).match(/^([^@\s]+)@([^@\s]+)$/);

    if (!match) return null;

    const [, user, domain] = match;

    if (DOMAIN_FIX[domain]) return { list: [`${user}@${DOMAIN_FIX[domain]}`], block: false };
    if (AMBIGUOUS[domain]) return { list: AMBIGUOUS[domain].map((fix) => `${user}@${fix}`), block: false };

    const parts = domain.split('.');

    if (parts.length === 1) {
      return { list: PROVIDERS.includes(domain) ? [`${user}@${domain}.com`] : [], block: true };
    }

    const ending = parts.pop();
    const base = parts.join('.');

    if (PROVIDERS.includes(base) && BAD_ENDING.test(ending)) return { list: [`${user}@${base}.com`], block: false };

    return null;
  }

  class EmailCheck {
    constructor(form, input) {
      this.form = form;
      this.input = input;
      this.kept = '';
      this.active = null;
      this.renderedKey = '';

      this.hint = document.createElement('div');
      this.hint.className = 'carpe-contact-form-bs__email-hint';
      this.hint.id = `${form.id}-email-hint`;
      this.hint.setAttribute('aria-live', 'polite');
      this.hint.setAttribute('aria-atomic', 'true');
      input.insertAdjacentElement('afterend', this.hint);
      input.setAttribute('aria-describedby', [input.getAttribute('aria-describedby'), this.hint.id].filter(Boolean).join(' '));
      this.lastState = this.getState();

      this.onBlur = () => this.check();
      this.onInput = () => {
        if (this.active) this.check();
      };
      /* capture, so a paused submit never reaches Basin's submit handler */
      this.onSubmit = (event) => {
        if (!this.check()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        input.focus();
      };

      input.addEventListener('blur', this.onBlur);
      input.addEventListener('input', this.onInput);
      form.addEventListener('submit', this.onSubmit, true);
    }

    destroy() {
      this.input.removeEventListener('blur', this.onBlur);
      this.input.removeEventListener('input', this.onInput);
      this.form.removeEventListener('submit', this.onSubmit, true);
      this.hint.remove();
      this.input.setAttribute('aria-describedby', (this.input.getAttribute('aria-describedby') || '').replace(this.hint.id, '').trim());
    }

    getState() {
      return {
        formId: this.form.id,
        email: this.input.value,
        suggestions: this.active ? [...this.active.list] : [],
        blocking: Boolean(this.active && this.active.block),
        kept: this.kept,
      };
    }

    check() {
      const value = normalize(this.input.value);
      const suggestion = value === this.kept ? null : suggest(value);
      const previous = this.lastState;
      const previousKey = this.renderedKey;

      this.active = suggestion;
      this.render();
      this.lastState = this.getState();

      if (this.renderedKey !== previousKey) {
        document.dispatchEvent(new CustomEvent('contact-email:change', {
          bubbles: true,
          detail: { state: this.lastState, previous },
        }));
      }

      return suggestion;
    }

    accept(address) {
      this.input.value = address;
      this.check();
      this.input.focus();
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    keep() {
      this.kept = normalize(this.input.value);
      this.check();
      this.input.focus();
    }

    /* Rebuilding on an unchanged prompt would swap the button out mid-click: pressing it blurs
       the input, blur re-checks, and the mouseup lands on a new element, so no click fires. */
    render() {
      const key = this.active ? JSON.stringify(this.active) : '';

      if (key === this.renderedKey) return;

      this.renderedKey = key;
      this.hint.replaceChildren();

      if (!this.active) return;

      const { list, block } = this.active;

      if (!list.length) {
        this.hint.textContent = 'Please check your email address. It looks like it’s missing an ending like .com';

        return;
      }

      this.hint.append('Did you mean ');
      list.forEach((address, index) => {
        if (index) this.hint.append(' or ');
        this.hint.append(this.button(address, () => this.accept(address)));
      });
      this.hint.append('?');

      if (!block) {
        const keep = this.button('Keep as typed', () => this.keep());

        keep.className = 'carpe-contact-form-bs__email-keep';
        this.hint.append(keep);
      }
    }

    button(label, onClick) {
      const button = document.createElement('button');

      button.type = 'button';
      button.textContent = label;
      button.addEventListener('click', onClick);

      return button;
    }
  }

  const ContactEmailCheckManager = {
    instances: new Map(),

    hydrate(scope = document) {
      scope.querySelectorAll('form[data-contact-form]').forEach((form) => {
        const input = form.querySelector('input[type="email"][name="email"]');

        if (!input) return;

        const existing = this.instances.get(form.id);

        if (existing && existing.form.isConnected) return;
        if (existing) existing.destroy();

        this.instances.set(form.id, new EmailCheck(form, input));
      });
    },

    destroyWithin(scope = document) {
      this.instances.forEach((instance, formId) => {
        if (!scope.contains(instance.form)) return;

        instance.destroy();
        this.instances.delete(formId);
      });
    },
  };

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });

      return;
    }

    callback();
  }

  ready(() => ContactEmailCheckManager.hydrate(document));

  document.addEventListener('shopify:section:load', (event) => {
    if (event && event.target) ContactEmailCheckManager.hydrate(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event && event.target) ContactEmailCheckManager.destroyWithin(event.target);
  });

  facade.hydrate = (scope) => ContactEmailCheckManager.hydrate(scope || document);
  facade.destroyWithin = (scope) => ContactEmailCheckManager.destroyWithin(scope || document);
  facade.instances = ContactEmailCheckManager.instances;
  facade.get = (formId) => ContactEmailCheckManager.instances.get(formId);
  facade.getState = (formId) => {
    const instance = ContactEmailCheckManager.instances.get(formId);

    return instance ? instance.getState() : null;
  };
  facade.suggest = suggest;
  window.ContactEmailCheck = facade;
})();
